import s from '#components/blocks/user/user.module.scss';
import PageTitle from '#components/common/page-title/PageTitle';
import Button from '#components/controls/buttons/button/Button';
import { FC, ReactNode } from 'react';
import Logo from '#components/common/logo/Logo';
import { useOvermindActions, useOvermindState } from '#store/overmind';
import IconButton from '#components/controls/icon-button/IconButton';
import Close from '#components/icons/Close';
import Typography from '#components/typography/Typography';

type ButtonProps = {
  isOutline?: boolean;
  isSecondary?: boolean;
  text?: string;
};

type Props = {
  children: ReactNode;
  title: string;
  subtitle?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryClick?: () => void;
  onSecondaryClick: () => void;
  isSubmitDisabled?: boolean;
  firstButtonProps?: ButtonProps;
  secondButtonProps?: ButtonProps;
  onClose?: () => void;
};

const UserContainer: FC<Props> = ({
  title,
  subtitle,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryClick,
  onSecondaryClick,
  isSubmitDisabled,
  children,
  firstButtonProps = { isOutline: true, isSecondary: true },
  secondButtonProps = { isOutline: false },
  onClose,
}) => {
  const showFirstButton = secondaryButtonText || firstButtonProps.text;
  const showSecondButton = primaryButtonText || secondButtonProps.text;
  const { hideProfileSelection } = useOvermindActions().app;
  const { profile } = useOvermindState().user;

  const handleLogoClick = (event) => {
    event.preventDefault();
    if (profile) {
      hideProfileSelection();
    }
  };

  return (
    <div className={s.host}>
      <div className={s.logo}>
        <Logo isSizeXl onClick={handleLogoClick} />
      </div>

      <form className={s.form} onSubmit={(e) => e.preventDefault()}>
        <fieldset className={s.formInner}>
          <PageTitle
            element="legend"
            title={title}
            isCentered
            classes="tw-mb-8 xxlarge-up:tw-mb-20"
          />
          {subtitle && (
            <Typography
              fontWeight="bold"
              classes="tw-text-h3 xlarge-up:tw-text-h2 tw-text-center tw-mb-12"
            >
              {subtitle}
            </Typography>
          )}
          <div className="tw-my-auto">{children}</div>
          {(showFirstButton || showSecondButton) && (
            <div className="tw-grid tw-gap-4 small:tw-grid-cols-1 medium-up:tw-mx-auto medium-up:tw-grid-flow-col tw-pb-8 tw-mt-8">
              {showFirstButton && (
                <Button
                  classes="tw-min-w-30"
                  type="submit"
                  text={secondaryButtonText}
                  onClick={onSecondaryClick}
                  {...firstButtonProps}
                />
              )}
              {showSecondButton && (
                <Button
                  classes="tw-min-w-30"
                  dataTestId="profile-submit-button"
                  type="submit"
                  text={primaryButtonText}
                  onClick={onPrimaryClick}
                  disabled={isSubmitDisabled}
                  {...secondButtonProps}
                />
              )}
            </div>
          )}
        </fieldset>
      </form>
      {!!onClose && (
        <IconButton
          onClick={onClose}
          transparent={true}
          classes="tw-absolute tw-top-8 tw-right-8 tw-z-10"
        >
          <Close size={40} />
        </IconButton>
      )}
    </div>
  );
};

export default UserContainer;
